import React, { useState, useEffect } from 'react';
import ReactionBar from './ReactionBar';
import UserProfileTooltip from './UserProfileTooltip';
import CommentSection from './CommentSection';

const API_BASE = process.env.REACT_APP_API_BASE;
const API_KEY = process.env.REACT_APP_API_KEY;

function SocialReviewItem({ review, user, onReactionUpdate }) {
  return (
    <div className="border border-gray-300 rounded-lg p-4 mb-4 bg-white shadow-sm">
      <div className="flex justify-between items-center mb-2">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-semibold text-indigo-600">{review.name}</h3>
          {review.imdb_url && (
            <a
              href={review.imdb_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-400 hover:bg-yellow-500 text-black text-xs font-bold rounded transition-colors"
              title="View on IMDb"
            >
              IMDb
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          )}
        </div>
        <span className="text-yellow-500 font-bold">{review.rating.toFixed(1)}</span>
      </div>
      <p className="text-gray-700 whitespace-pre-line">
        {review.review || <i className="text-gray-400">No review text provided</i>}
      </p>
      <div className="mt-3 text-sm text-gray-500 flex justify-between items-center">
        <span>{new Date(review.created_at).toLocaleDateString()}</span>
        {review.username ? (
          <UserProfileTooltip username={review.username}>
            <span className="text-indigo-500 font-medium">@{review.username}</span>
          </UserProfileTooltip>
        ) : (
          <span className="text-gray-400 font-medium">Anonymous</span>
        )}
      </div>

      <ReactionBar
        reviewId={review.id}
        reactions={review.reactions || {}}
        totalReactions={review.total_reactions || 0}
        userReaction={review.user_reaction}
        user={user}
        onReactionUpdate={onReactionUpdate}
      />

      <CommentSection
        reviewId={review.id}
        user={user}
        initialCommentCount={review.total_comments || 0}
      />
    </div>
  );
}

function SkeletonReviewItem() {
  return (
    <div className="border border-gray-300 rounded-lg p-4 mb-4 bg-white shadow-sm animate-pulse">
      <div className="flex justify-between items-center mb-2">
        <div className="h-6 bg-gray-200 rounded w-1/3"></div>
        <div className="h-6 bg-gray-200 rounded w-12"></div>
      </div>
      <div className="h-16 bg-gray-200 rounded mb-3"></div>
      <div className="flex justify-between items-center text-sm">
        <div className="h-4 bg-gray-200 rounded w-24"></div>
        <div className="h-4 bg-gray-200 rounded w-16"></div>
      </div>
    </div>
  );
}

export default function SocialFeed({ user }) {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchPublicReviews() {
      setError(null);
      try {
        const headers = {
          'X-API-Key': API_KEY,
          'Content-Type': 'application/json',
        };

        // Include user context for reactions if user is logged in
        if (user) {
          headers['X-User-ID'] = user.id;
        }

        const response = await fetch(`${API_BASE}/v1/reviews/public`, {
          headers,
        });
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.log('API Error Response:', errorData);
          throw new Error(errorData.detail || 'Failed to fetch reviews');
        }
        const data = await response.json();
        console.log('Review data from API:', data[0]);
        const publicReviews = data.filter(review => review.visibility === 'public');
        setReviews(publicReviews);
      } catch (err) {
        console.error('Fetch error:', err);
        setError(err.message || 'Unknown error');
      } finally {
        setLoading(false);
      }
    }
    fetchPublicReviews();
  }, [user]); // Refetch when user changes

  const handleReactionUpdate = (reviewId, updatedReactionData) => {
    setReviews(prevReviews =>
      prevReviews.map(review =>
        review.id === reviewId
          ? { ...review, ...updatedReactionData }
          : review
      )
    );
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto p-4">
          {[...Array(3)].map((_, i) => (
            <SkeletonReviewItem key={i} />
          ))}
        </div>
    );
  }

  if (error) return <div className="text-center text-red-600 py-8">Error: {error}</div>;
  if (!reviews.length) return <div className="text-center py-8 text-gray-600">No public reviews found.</div>;

  return (
    <div className="max-w-3xl mx-auto p-4">
      <h2 className="text-xl font-bold mb-6 text-cling-pink">Recent community reviews</h2>
        {reviews.map((review) => (
          <SocialReviewItem
            key={review.id}
            review={review}
            user={user}
            onReactionUpdate={handleReactionUpdate}
          />
        ))}
      </div>
  );
}

